import type { ThemeConfig } from 'antd';

/**
 * Company hue. Indigo for Sonarox — companies/DESIGN-LANGUAGE.md.
 *
 * Data inks are constant across all four companies so a chart reads the same
 * everywhere; only the accent moves between products.
 */
export const INK = {
	accent: '#8FA2FF',
	accentDeep: '#222F73',
	accentLight: '#2B3A8C',
	accentWash: '#1B2147',
	accentHover: '#A9B7FF',

	void: '#0B1022',
	base: '#0F0E1B',
	panel: '#151326',
	raised: '#1E1B34',

	text: '#E4E0F2',
	textDim: '#A9A3C4',
	textFaint: '#7E789C',

	line: '#272440',
	lineStrong: '#3A365A',

	gold: '#D8B26A',
	violet: '#9182E0',
	aqua: '#45BFA4',
	rose: '#E85C77',
	amber: '#E0A050',
} as const;

export const PAPER = {
	base: '#FAF9FE',
	panel: '#FFFFFF',
	raised: '#F2F0FA',
	field: '#FBFBFE',

	text: '#17142B',
	textDim: '#4A4468',
	textFaint: '#635C85',

	line: '#E4E2EE',
	lineStrong: '#C6C2D8',
	accentWash: '#ECEFFF',
} as const;

/** Status colours are identical on every surface (docs/DESIGN-LANGUAGE.md §2.2). */
export const STATUS = {
	ok: '#2F9E74',
	warn: '#D89A22',
	crit: '#D64545',
	muted: '#8A93B8',
} as const;

export const FONT_SANS =
	'Archivo, Inter, ui-sans-serif, system-ui, -apple-system, sans-serif';
export const FONT_MONO =
	'"Chivo Mono", "Geist Mono", ui-monospace, SFMono-Regular, monospace';

/**
 * Radii and spacing sit a step tighter than upstream's defaults. The product
 * is a dense instrument panel: rounded, roomy cards push the numbers apart and
 * make a screenful of series read as a marketing page.
 */
function tokens(isDarkMode: boolean): NonNullable<ThemeConfig['token']> {
	const surface = isDarkMode ? INK : PAPER;

	return {
		borderRadius: 2,
		borderRadiusLG: 0,
		borderRadiusSM: 2,
		borderRadiusXS: 2,

		controlHeight: 40,
		controlHeightLG: 44,
		controlHeightSM: 32,

		fontFamily: FONT_SANS,
		fontFamilyCode: FONT_MONO,
		fontSize: 14,
		fontSizeSM: 12,
		fontSizeLG: 16,
		fontSizeHeading1: 28,
		fontSizeHeading2: 22,
		fontSizeHeading3: 18,
		fontSizeHeading4: 15,
		fontSizeHeading5: 13,
		lineHeight: 1.43,

		colorPrimary: isDarkMode ? INK.accent : INK.accentLight,
		colorLink: isDarkMode ? INK.accent : INK.accentLight,
		colorPrimaryText: isDarkMode ? INK.accent : INK.accentLight,
		colorPrimaryBg: isDarkMode ? INK.accentWash : PAPER.accentWash,
		colorPrimaryHover: isDarkMode ? INK.accentHover : INK.accentDeep,
		colorTextLightSolid: isDarkMode ? INK.void : PAPER.panel,

		colorBgBase: surface.base,
		colorBgContainer: surface.panel,
		colorBgElevated: isDarkMode ? INK.raised : PAPER.panel,
		colorBgLayout: surface.base,
		colorBgSpotlight: isDarkMode ? INK.raised : PAPER.raised,

		colorText: surface.text,
		colorTextSecondary: surface.textDim,
		colorTextTertiary: surface.textFaint,
		colorTextQuaternary: surface.textFaint,
		colorTextHeading: surface.text,

		colorBorder: surface.lineStrong,
		colorBorderSecondary: surface.line,
		colorSplit: surface.line,

		colorSuccess: STATUS.ok,
		colorWarning: STATUS.warn,
		colorError: STATUS.crit,
		colorInfo: isDarkMode ? INK.accent : INK.accentLight,

		// Separation is surface colour plus 1 px rules; no shadows anywhere.
		boxShadow: 'none',
		boxShadowSecondary: 'none',
		boxShadowTertiary: 'none',

		wireframe: false,
	};
}

/**
 * Component overrides are merged per component, never wholesale — upstream's
 * block churns hardest on release and a replaced key loses their fixes.
 */
function components(
	isDarkMode: boolean,
): NonNullable<ThemeConfig['components']> {
	const surface = isDarkMode ? INK : PAPER;

	return {
		Layout: {
			bodyBg: surface.base,
			headerBg: surface.base,
			siderBg: INK.void,
		},
		Menu: {
			itemBg: 'transparent',
			subMenuItemBg: 'transparent',
			itemSelectedBg: isDarkMode ? INK.accentWash : PAPER.accentWash,
			itemSelectedColor: isDarkMode ? INK.accent : INK.accentLight,
			itemHoverBg: isDarkMode ? INK.accentWash : PAPER.accentWash,
		},
		Card: {
			colorBorderSecondary: surface.line,
			borderRadiusLG: 0,
		},
		Table: {
			headerBg: surface.panel,
			headerColor: surface.textDim,
			headerSplitColor: 'transparent',
			rowHoverBg: isDarkMode ? INK.accentWash : PAPER.accentWash,
			rowSelectedBg: isDarkMode ? INK.accentWash : PAPER.accentWash,
			borderColor: surface.line,
			cellPaddingBlock: 10,
			cellPaddingBlockMD: 10,
			cellPaddingBlockSM: 8,
		},
		Input: { colorBgContainer: isDarkMode ? INK.panel : PAPER.field },
		InputNumber: { colorBgContainer: isDarkMode ? INK.panel : PAPER.field },
		Button: {
			fontWeight: 500,
			primaryShadow: 'none',
			defaultShadow: 'none',
			dangerShadow: 'none',
		},
		Select: {
			optionSelectedBg: isDarkMode ? INK.accentWash : PAPER.accentWash,
			colorBgContainer: isDarkMode ? INK.panel : PAPER.field,
		},
		Dropdown: { colorBgElevated: isDarkMode ? INK.raised : PAPER.panel },
		Tooltip: { colorBgSpotlight: isDarkMode ? INK.raised : PAPER.text },
		Tag: {
			defaultBg: isDarkMode ? INK.accentWash : PAPER.accentWash,
			borderRadiusSM: 2,
		},
		Modal: { contentBg: surface.panel, headerBg: surface.panel },
		Drawer: { colorBgElevated: surface.panel },
		Progress: { defaultColor: isDarkMode ? INK.accent : INK.accentLight },
		Segmented: {
			itemSelectedBg: surface.text,
			itemSelectedColor: surface.base,
			colorBgLayout: surface.panel,
			borderRadius: 2,
			borderRadiusSM: 2,
		},
	};
}

export function brandTheme(isDarkMode: boolean): ThemeConfig {
	return { token: tokens(isDarkMode), components: components(isDarkMode) };
}

/** Deep-merges our tokens over upstream's without discarding theirs. */
export function withBrand(
	upstream: ThemeConfig,
	isDarkMode: boolean,
): ThemeConfig {
	const ours = brandTheme(isDarkMode);
	const merged: NonNullable<ThemeConfig['components']> = {
		...upstream.components,
	};

	Object.entries(ours.components ?? {}).forEach(([name, override]) => {
		const key = name as keyof NonNullable<ThemeConfig['components']>;
		merged[key] = {
			...(upstream.components?.[key] ?? {}),
			...override,
		} as never;
	});

	return {
		...upstream,
		token: { ...upstream.token, ...ours.token },
		components: merged,
	};
}
